import React, { useCallback, useContext, createContext, useRef } from 'react'

const WorkflowHierarchyContext = createContext()

export const WorkflowHierarchyProvider = ({ children }) => {
  const hierarchyRegistry = useRef(new Map())

  // Register a workflow (for the main workflow)
  const registerWorkflow = useCallback((workflowId, parentId = null) => {
    if (!workflowId) return
    const existing = hierarchyRegistry.current.get(workflowId) || {
      parents: new Set(),
      children: new Set(),
      subWorkflowIds: new Set() // To store all nested subworkflows
    }

    if (parentId) {
      existing.parents.add(parentId)
    }

    hierarchyRegistry.current.set(workflowId, existing)

    // If there is a parent, also register the inverse relationship
    if (parentId) {
      const parent = hierarchyRegistry.current.get(parentId) || {
        parents: new Set(),
        children: new Set(),
        subWorkflowIds: new Set()
      }
      parent.children.add(workflowId)
      hierarchyRegistry.current.set(parentId, parent)
    }
  }, [])

  // Register a workflow as a child of another, including all its subworkflows
  const registerChildWorkflow = useCallback((parentId, childId, subWorkflowIds = []) => {
    if (!parentId || !childId) return

    // Register in the parent
    const parent = hierarchyRegistry.current.get(parentId) || {
      parents: new Set(),
      children: new Set(),
      subWorkflowIds: new Set()
    }
    parent.children.add(childId)

    // Register all the child's subworkflows in the parent
    subWorkflowIds.forEach(subId => {
      parent.subWorkflowIds.add(subId)
    })

    hierarchyRegistry.current.set(parentId, parent)

    // Register in the child
    const child = hierarchyRegistry.current.get(childId) || {
      parents: new Set(),
      children: new Set(),
      subWorkflowIds: new Set()
    }
    child.parents.add(parentId)

    // Register the child's subworkflows
    subWorkflowIds.forEach(subId => {
      child.subWorkflowIds.add(subId)
    })

    hierarchyRegistry.current.set(childId, child)
  }, [])

  // Unregister a workflow
  const unregisterWorkflow = useCallback((workflowId, parentId = null) => {
    if (!workflowId) return

    const existing = hierarchyRegistry.current.get(workflowId)
    if (existing) {
      if (parentId) {
        existing.parents.delete(parentId)

        // Also remove from the parent
        const parent = hierarchyRegistry.current.get(parentId)
        if (parent) {
          parent.children.delete(workflowId)
          // Also remove this workflow's subworkflows
          existing.subWorkflowIds?.forEach(subId => {
            parent.subWorkflowIds.delete(subId)
          })
          if (parent.children.size === 0 && parent.parents.size === 0) {
            hierarchyRegistry.current.delete(parentId)
          }
        }
      }

      if (existing.parents.size === 0 && existing.children.size === 0) {
        hierarchyRegistry.current.delete(workflowId)
      }
    }
  }, [])

  // Unregister a workflow as a child of another
  const unregisterChildWorkflow = useCallback((parentId, childId, subWorkflowIds = []) => {
    if (!parentId || !childId) return

    // Remove from the parent
    const parent = hierarchyRegistry.current.get(parentId)
    if (parent) {
      parent.children.delete(childId)
      // Remover los subworkflows del hijo
      subWorkflowIds.forEach(subId => {
        parent.subWorkflowIds.delete(subId)
      })
      if (parent.children.size === 0 && parent.parents.size === 0) {
        hierarchyRegistry.current.delete(parentId)
      }
    }

    // Remove from the child
    const child = hierarchyRegistry.current.get(childId)
    if (child) {
      child.parents.delete(parentId)
      if (child.parents.size === 0 && child.children.size === 0) {
        hierarchyRegistry.current.delete(childId)
      }
    }
  }, [])

  // Check if it would create a cycle
  const checkWouldCreateCycle = useCallback((parentId, candidateId) => {
    const visited = new Set()

    const checkIfAncestor = (currentId, targetId) => {
      if (currentId === targetId) {
        return true
      }
      if (visited.has(currentId)) return false

      visited.add(currentId)

      const workflow = hierarchyRegistry.current.get(currentId)
      if (!workflow || !workflow.parents) return false

      // Check all parents recursively
      for (const parentId of workflow.parents) {
        if (checkIfAncestor(parentId, targetId)) {
          return true
        }
      }

      return false
    }

    // Check if the candidate is an ancestor of the parent
    return checkIfAncestor(parentId, candidateId)
  }, [])
  // Check if a workflow can be added as a child, verifying all its subworkflows
  const canAddWorkflow = useCallback((parentId, candidateId, candidateSubWorkflowIds = []) => {
    if (!parentId || !candidateId) return true

    // No permitir auto-referencia
    if (parentId === candidateId) {
      return false
    }

    // Check if the candidate or any of its subworkflows are already ancestors of the parent
    const allIdsToCheck = [candidateId, ...candidateSubWorkflowIds]

    for (const idToCheck of allIdsToCheck) {
      const wouldCreateCycle = checkWouldCreateCycle(parentId, idToCheck)
      if (wouldCreateCycle) {
        return false
      }
    }

    return true
  }, [checkWouldCreateCycle])
  const contextValue = {
    registerWorkflow,
    registerChildWorkflow,
    unregisterWorkflow,
    unregisterChildWorkflow,
    canAddWorkflow
  }

  return (
    <WorkflowHierarchyContext.Provider value={contextValue}>
      {children}
    </WorkflowHierarchyContext.Provider>
  )
}

export const useWorkflowHierarchy = () => {
  const context = useContext(WorkflowHierarchyContext)
  if (!context) {
    throw new Error('useWorkflowHierarchy must be used within a WorkflowHierarchyProvider')
  }
  return context
}
